import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getOrganizationCareTeamMemberCacheKey,
    getOrganizationCareTeamMembersCacheKey,
    longTermCacheStaleTime,
    onCopyOrgCareTeamMemberSuccess,
    onUpdateOrganizationCareTeamMembers,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('useOrganizationCareTeamMembers', {
    careTeamMembersLoadFailure: 'Failed to load care team members',
    careTeamMemberLoadFailure: 'Failed to load care team member',
    deleteCareTeamMemberFailure: 'Failed to delete care team member',
    deleteCareTeamMemberSuccess: 'Care team member deleted successfully',
    deleteCareTeamMembersFailure: 'Failed to delete care team members',
    deleteCareTeamMembersSuccess: 'Care team members deleted successfully',
    createCareTeamMemberFailure: 'Failed to create care team member',
    createCareTeamMemberSuccess: 'Care team member created successfully',
    updateCareTeamMemberFailure: 'Failed to update care team member',
    updateCareTeamMemberSuccess: 'Care team member updated successfully',
    copyCareTeamMemberToClientsFailure: 'Failed to copy care team member to clients',
    copyCareTeamMemberToClientsSuccess: 'Care team member copied to client{pluralProfile} successfully',
    failedToSendToClients:
        'Failed to send care team member to {count} client(s). Your organization may not have proper permissions to some of the selected clients.',
});

export type UseOrganizationCareTeamMembersArgs = {
    organizationCareTeamMemberId?: string;
    onDeleteCareTeamMember?: () => void;
    onCareTeamMembersUpdate?: () => void;
    onCreateCareTeamMember?: () => void;
    onUpdateCareTeamMember?: () => void;
    onCopyCareTeamMemberToClientsSuccess?: () => void;
};

export type UpdateCareTeamMemberArgs = {
    careTeamMemberId: string;
    update: APITypes.organizationService.UpdateOrganizationCareTeamMemberRequest;
};

export type CopyCareTeamMemberToClientsArgs = {
    destinationMedicalProfileIds: string[];
    organizationCareTeamMemberIds: string[];
};

export type UseOrganizationCareTeamMembersReturnValue = {
    organizationCareTeamMembers: APITypes.organizationService.OrganizationCareTeamMemberDetails[];
    isLoadingOrganizationCareTeamMembers: boolean;
    isFetchingOrganizationCareTeamMembers: boolean;
    organizationCareTeamMember: APITypes.organizationService.OrganizationCareTeamMemberDetails | null;
    isLoadingOrganizationCareTeamMember: boolean;
    deleteCareTeamMember: (careTeamMemberId: string) => void;
    isDeletingCareTeamMember: boolean;
    deleteCareTeamMembers: (careTeamMemberIds: string[]) => void;
    isDeletingCareTeamMembers: boolean;
    createCareTeamMember: (
        careTeamMember: APITypes.organizationService.CreateOrganizationCareTeamMemberRequest,
    ) => void;
    isCreatingCareTeamMember: boolean;
    updateCareTeamMember: (args: UpdateCareTeamMemberArgs) => void;
    isUpdatingCareTeamMember: boolean;
    copyCareTeamMemberToClients: (args: CopyCareTeamMemberToClientsArgs) => void;
    isCopyingCareTeamMemberToClients: boolean;
};

const emptyArr: any[] = [];

export default function useOrganizationCareTeamMembers({
    organizationCareTeamMemberId,
    onDeleteCareTeamMember,
    onCareTeamMembersUpdate,
    onCreateCareTeamMember,
    onUpdateCareTeamMember,
    onCopyCareTeamMemberToClientsSuccess,
}: UseOrganizationCareTeamMembersArgs): UseOrganizationCareTeamMembersReturnValue {
    const organizationId = useOrganizationId();
    const notify = useNotify();

    const organizationCareTeamMembersQuery = useQuery({
        queryKey: getOrganizationCareTeamMembersCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListOrganizationCareTeamMembersResponse>(
                urls.api.organizations.careTeamMembers.careTeamMembers(organizationId),
            );
        },
        staleTime: longTermCacheStaleTime,
    });
    useQueryErrorNotify(organizationCareTeamMembersQuery, intl('careTeamMembersLoadFailure'));

    const organizationCareTeamMemberQuery = useQuery({
        queryKey: organizationCareTeamMemberId
            ? getOrganizationCareTeamMemberCacheKey(organizationCareTeamMemberId)
            : [],
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.OrganizationCareTeamMemberDetails>(
                urls.api.organizations.careTeamMembers.careTeamMember(organizationId, organizationCareTeamMemberId!),
            );
        },
        enabled: !!organizationCareTeamMemberId,
    });
    useQueryErrorNotify(organizationCareTeamMemberQuery, intl('careTeamMemberLoadFailure'));

    const deleteCareTeamMember = useMutation({
        mutationFn: async (careTeamMemberId: string) => {
            return await appAPI.del(
                urls.api.organizations.careTeamMembers.careTeamMember(organizationId, careTeamMemberId),
            );
        },
        onSuccess: () => {
            notify('success', intl('deleteCareTeamMemberSuccess'));
            onUpdateOrganizationCareTeamMembers.resetCache({})();
            onDeleteCareTeamMember?.();
        },
        onError: () => {
            notify('error', intl('deleteCareTeamMemberFailure'));
        },
    });

    const deleteCareTeamMembers = useMutation({
        mutationFn: async (careTeamMemberIds: string[]) => {
            await Promise.all(
                careTeamMemberIds.map(async (careTeamMemberId) => {
                    await appAPI.del(
                        urls.api.organizations.careTeamMembers.careTeamMember(organizationId, careTeamMemberId),
                    );
                }),
            );
        },
        onSuccess: () => {
            notify('success', intl('deleteCareTeamMembersSuccess'));
            onUpdateOrganizationCareTeamMembers.resetCache({})();
            onCareTeamMembersUpdate?.();
        },
        onError: () => {
            notify('error', intl('deleteCareTeamMembersFailure'));
        },
    });

    const createCareTeamMember = useMutation({
        mutationFn: async (careTeamMember: APITypes.organizationService.CreateOrganizationCareTeamMemberRequest) => {
            return await appAPI.post(urls.api.organizations.careTeamMembers.careTeamMembers(organizationId), {
                body: careTeamMember,
            });
        },
        onSuccess: () => {
            notify('success', intl('createCareTeamMemberSuccess'));
            onUpdateOrganizationCareTeamMembers.resetCache({})();
            onCreateCareTeamMember?.();
        },
        onError: () => {
            notify('error', intl('createCareTeamMemberFailure'));
        },
    });

    const updateCareTeamMember = useMutation({
        mutationFn: async ({ careTeamMemberId, update }: UpdateCareTeamMemberArgs) => {
            return await appAPI.put(
                urls.api.organizations.careTeamMembers.careTeamMember(organizationId, careTeamMemberId),
                { body: update },
            );
        },
        onSuccess: () => {
            notify('success', intl('updateCareTeamMemberSuccess'));
            onUpdateOrganizationCareTeamMembers.resetCache({})();
            onUpdateCareTeamMember?.();
        },
        onError: () => {
            notify('error', intl('updateCareTeamMemberFailure'));
        },
    });

    const copyCareTeamMemberToClients = useMutation({
        mutationFn: async ({
            destinationMedicalProfileIds,
            organizationCareTeamMemberIds,
        }: CopyCareTeamMemberToClientsArgs) => {
            return await Promise.all(
                organizationCareTeamMemberIds.map((_) =>
                    appAPI.post<APITypes.organizationService.CopyOrganizationCareTeamMemberToOrganizationMedicalProfilesResponse>(
                        urls.api.organizations.careTeamMembers.copyCareTeamMemberToClients(organizationId, _),
                        {
                            body: {
                                destinationMedicalProfileIds,
                            },
                        },
                    ),
                ),
            );
        },
        onSuccess: (data, { destinationMedicalProfileIds }) => {
            const failedProfileSet = new Set<string>();
            data.forEach((response) => {
                if (response.noAccessMedicalProfileIds?.length) {
                    response.noAccessMedicalProfileIds.forEach((id) => failedProfileSet.add(id));
                }
            });
            const failedCount = failedProfileSet.size;

            if (failedCount) {
                notify('warn', intl('failedToSendToClients', { count: failedCount }));
            } else {
                notify(
                    'success',
                    intl('copyCareTeamMemberToClientsSuccess', {
                        pluralProfile: destinationMedicalProfileIds.length > 1 ? 's' : '',
                    }),
                );
            }
            onCopyOrgCareTeamMemberSuccess.resetCache({ profileIds: destinationMedicalProfileIds })();
            onCopyCareTeamMemberToClientsSuccess?.();
        },
        onError: () => {
            notify('error', intl('copyCareTeamMemberToClientsFailure'));
        },
    });

    return {
        organizationCareTeamMembers: organizationCareTeamMembersQuery.data?.items || emptyArr,
        isLoadingOrganizationCareTeamMembers: organizationCareTeamMembersQuery.isLoading,
        isFetchingOrganizationCareTeamMembers: organizationCareTeamMembersQuery.isFetching,
        organizationCareTeamMember: organizationCareTeamMemberQuery.data || null,
        isLoadingOrganizationCareTeamMember: organizationCareTeamMemberQuery.isLoading,
        deleteCareTeamMember: deleteCareTeamMember.mutate,
        isDeletingCareTeamMember: deleteCareTeamMember.isPending,
        deleteCareTeamMembers: deleteCareTeamMembers.mutate,
        isDeletingCareTeamMembers: deleteCareTeamMembers.isPending,
        createCareTeamMember: createCareTeamMember.mutate,
        isCreatingCareTeamMember: createCareTeamMember.isPending,
        updateCareTeamMember: updateCareTeamMember.mutate,
        isUpdatingCareTeamMember: updateCareTeamMember.isPending,
        copyCareTeamMemberToClients: copyCareTeamMemberToClients.mutate,
        isCopyingCareTeamMemberToClients: copyCareTeamMemberToClients.isPending,
    };
}
